import { LiquidGlassView, isLiquidGlassSupported } from '@callstack/liquid-glass';
import React from 'react';
import { Pressable } from 'react-native';

import { Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors, withOpacity } from '@/theme/colors';

export type OnboardingRoleOption = {
  value: string;
  label: string;
};

type OnboardingRoleSelectorProps = {
  options: OnboardingRoleOption[];
  value: string | null;
  onChange: (value: string) => void;
};

/**
 * Horizontal pill selector for the guardian's relationship to the player
 * (Parent / Grandparent / Guardian / Other). Single-select.
 *
 * On iOS 26+ each pill is a native Liquid Glass surface. Elsewhere (Android /
 * iOS < 26) the Pressable itself is the flat-filled surface — deliberately with
 * no wrapper view and no `overflow: 'hidden'`, since that combination drops the
 * label on Android when the border toggles as selection changes.
 */
export function OnboardingRoleSelector({ options, value, onChange }: OnboardingRoleSelectorProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <Stack direction="row" width={'100%'} gap={scale(4)}>
      {options.map(option => {
        const selected = option.value === value;
        const label = (
          <Text
            variant={
              selected
                ? 'Font Guardian App/Subtitle/Subtitle 12 Semibold'
                : 'Font Guardian App/Subtitle/Subtitle 12 Regular'
            }
            style={selected ? styles.labelSelected : styles.label}>
            {option.label}
          </Text>
        );

        if (isLiquidGlassSupported) {
          // iOS 26+: the pill surface is the native glass material. The selected
          // pill is tinted so it reads as filled; the border marks selection.
          return (
            <Pressable key={option.value} onPress={() => onChange(option.value)}>
              <LiquidGlassView
                style={[styles.pill, styles.glassClip, selected && styles.glassSelectedBorder]}
                effect="regular"
                interactive
                colorScheme="light"
                tintColor={selected ? withOpacity(colors['0'], 0.4) : undefined}>
                {label}
              </LiquidGlassView>
            </Pressable>
          );
        }

        // Android / iOS < 26: flat fill on the Pressable itself (matches the
        // original structure that rendered correctly across selection changes).
        return (
          <Pressable
            key={option.value}
            onPress={() => onChange(option.value)}
            style={[styles.pill, selected ? styles.pillSelected : styles.pillUnselected]}>
            {label}
          </Pressable>
        );
      })}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  pill: {
    height: scale(40),
    paddingHorizontal: scale(16),
    paddingVertical: scale(10),
    borderRadius: scale(40),
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Clips the glass material to the rounded pill. iOS-only: pairing it with the
  // border toggle on Android is what makes the label vanish on re-layout.
  glassClip: {
    overflow: 'hidden',
  },
  // Selection outline drawn over the glass surface (iOS).
  glassSelectedBorder: {
    borderWidth: 1,
    borderColor: colors['Level 1'],
  },
  // Flat fills for the non-glass path (Android / iOS < 26).
  pillSelected: {
    backgroundColor: colors['0'],
    borderWidth: 1,
    borderColor: colors['Level 1'],
  },
  pillUnselected: {
    backgroundColor: withOpacity(colors['0'], 0.6),
  },
  label: {
    color: colors['10'],
  },
  labelSelected: {
    color: guardianColors['Main/Main'],
  },
}));

export default OnboardingRoleSelector;
